import { Action, Reducer } from 'redux';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as TeamsState from './TeamsState';
import { TokenHelper } from '../TokenHelper';
import { AppThunkAction } from '.';
import { SortHelper } from '../SortHelper';
import { KnownContactsAction } from './contacts';
import { KnownCustomersAction } from './customers';
import { Consultant, ConsultantDocument, ConsultantInvoice, FixePriceConsultant, PeppolStatus, ConsultantPeppolReceivedCreditNote, ConsultantPeppolReceivedInvoice } from '../interfaces/Consultant';
import { KnownDataAction } from './Data';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface ConsultantsState {
    consultants: Consultant[] | undefined;
    consultantsPhotoLoaded: boolean;
    consultantEdit: Consultant | null;
    consultantActivityPeriodesEdit: Consultant | null;
    consultantsLoading: boolean;
    consultantsInvoices: ConsultantInvoice[] | undefined;
    consultantInvoiceEdit: ConsultantInvoice | null;
    consultantsInvoicesLoading: boolean;
    fixePriceConsultants: FixePriceConsultant[] | undefined;
    fixePriceConsultantsLoading: boolean;
    consultantDocuments: ConsultantDocument[] | undefined;
    consultantDocumentsLoading: boolean;
    consultantDocumentCreate: ConsultantDocument | null;
    consultantDocumentEdit: ConsultantDocument | null;
    consultantDocumentDelete: ConsultantDocument | null;
    consultantPeppolReceivedInvoicedeletion: ConsultantPeppolReceivedInvoice | null;
    consultantPeppolReceivedInvoices: ConsultantPeppolReceivedInvoice[] | undefined;
    consultantPeppolReceivedInvoicesLoading: boolean;
    consultantPeppolReceivedCreditNotes: ConsultantPeppolReceivedCreditNote[] | undefined;
    consultantPeppolReceivedCreditNotesLoading: boolean;

    consultantQuickView: Consultant | null;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

interface RequestConsultantsAction { type: 'REQUEST_CONSULTANTS' }
interface ReceiveConsultantsAction { type: 'RECEIVE_CONSULTANTS'; consultants: Consultant[]; }
interface EditConsultantAction { type: 'EDIT_CONSULTANT'; consultant: Consultant | null; }
interface EditConsultantActivityPeriodesAction { type: 'EDITACTIVITYPERIODES_CONSULTANT'; consultant: Consultant | null; }
interface PatchConsultantAction { type: 'PATCH_CONSULTANT'; consultant: Consultant; }
interface PatchConsultantPhotoAction { type: 'PATCH_CONSULTANT_PHOTO'; consultantid: string, photo: any; }
interface RequestConsultantPhotoAction { type: 'REQUEST_CONSULTANT_PHOTO'; }
interface ReloadConsultantsInvoicesAction { type: 'RELOAD_CONSULTANTSINVOICES' }
interface RequestConsultantsInvoicesAction { type: 'REQUEST_CONSULTANTSINVOICES' }
interface ReceiveConsultantsInvoicesAction { type: 'RECEIVE_CONSULTANTSINVOICES'; consultantsInvoices: ConsultantInvoice[]; }
interface EditConsultantInvoiceAction { type: 'EDIT_CONSULTANTINVOICE'; consultantInvoice: ConsultantInvoice | null; }
interface PatchConsultantInvoiceAction { type: 'PATCH_CONSULTANTINVOICE'; consultantInvoice: ConsultantInvoice; }
interface RequestFixePriceConsultantAction { type: 'REQUEST_FIXEPRICECONSULTANT' }
interface ReceiveFixePriceConsultantAction { type: 'RECEIVE_FIXEPRICECONSULTANT'; fixePriceConsultants: FixePriceConsultant[]; }
interface QuickViewConsultantAction { type: 'QV_CONSULTANT'; consultant: Consultant | null; }
interface RequestConsultantsDocumentsAction { type: 'REQUEST_CONSULTANTSDOCUMENTS' }
interface ReceiveConsultantsDocumentsAction { type: 'RECEIVE_CONSULTANTSDOCUMENTS'; consultantsDocuments: ConsultantDocument[]; }
interface EditConsultantDocumentAction { type: 'EDIT_CONSULTANTDOCUMENT'; consultantDocument: ConsultantDocument | null; }
interface CreateConsultantDocumentAction { type: 'CREATE_CONSULTANTDOCUMENT'; consultantDocument: ConsultantDocument | null; }
interface PatchConsultantDocumentAction { type: 'PATCH_CONSULTANTDOCUMENT'; consultantDocument: ConsultantDocument; }
interface requestConsultantDocumentdeletion { type: 'DELETE_CONSULTANTDOCUMENT'; consultantDocument: ConsultantDocument | null; }
interface requestConsultantPeppolReceivedInvoicedeletion { type: 'DELETE_CONSULTANTPEPPOLRECEIVEDINVOICE'; consultantDocument: ConsultantPeppolReceivedInvoice | null; }
interface patchConsultantDocumentDeletionEdition { type: 'PATCHDELETE_CONSULTANTDOCUMENT'; consultantDocument: ConsultantDocument; }
interface patchConsultantPeppolReceivedInvoiceDeletionEdition { type: 'PATCHDELETE_CONSULTANTPEPPOLRECEIVEDINVOICE'; consultantDocument: ConsultantPeppolReceivedInvoice; }
interface RequestConsultantPeppolStatus { type: 'REQUEST_CONSULTANT_PEPPOLSTATUS'; consultant: Consultant; }
interface ReceiveConsultantPeppolStatus { type: 'RECEIVE_CONSULTANT_PEPPOLSTATUS'; peppolStatus: PeppolStatus; consultant: Consultant; }
interface RequestConsultantPeppolReceivedInvoices { type: 'REQUEST_CONSULTANT_PEPPOLRECEIVEDINVOICES'; }
interface ReceiveConsultantPeppolReceivedInvoices { type: 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDINVOICES'; consultantsDocuments: ConsultantPeppolReceivedInvoice[]; }
interface PatchReceiveConsultantPeppolReceivedInvoices { type: 'PATCH_CONSULTANT_PEPPOLRECEIVEDINVOICES'; consultantDocument: ConsultantPeppolReceivedInvoice; }
interface RequestConsultantPeppolReceivedCreditNotes { type: 'REQUEST_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES'; }
interface ReceiveConsultantPeppolReceivedCreditNotes { type: 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES'; consultantsDocuments: ConsultantPeppolReceivedCreditNote[]; }
interface PatchConsultantPeppolReceivedCreditNotes { type: 'PATCH_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES'; consultantDocument: ConsultantPeppolReceivedCreditNote; }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownConsultantAction = RequestConsultantsAction | ReceiveConsultantsAction | EditConsultantAction | EditConsultantActivityPeriodesAction | PatchConsultantAction | RequestConsultantPhotoAction | PatchConsultantPhotoAction
    | ReloadConsultantsInvoicesAction | RequestConsultantsInvoicesAction | ReceiveConsultantsInvoicesAction | EditConsultantInvoiceAction | PatchConsultantInvoiceAction
    | RequestFixePriceConsultantAction | ReceiveFixePriceConsultantAction
    | QuickViewConsultantAction
    | RequestConsultantsDocumentsAction | ReceiveConsultantsDocumentsAction | CreateConsultantDocumentAction | EditConsultantDocumentAction | PatchConsultantDocumentAction | requestConsultantDocumentdeletion | requestConsultantPeppolReceivedInvoicedeletion | patchConsultantDocumentDeletionEdition | patchConsultantPeppolReceivedInvoiceDeletionEdition
    | RequestConsultantPeppolStatus | ReceiveConsultantPeppolStatus
    | RequestConsultantPeppolReceivedInvoices | ReceiveConsultantPeppolReceivedInvoices | PatchReceiveConsultantPeppolReceivedInvoices
    | RequestConsultantPeppolReceivedCreditNotes | ReceiveConsultantPeppolReceivedCreditNotes | PatchConsultantPeppolReceivedCreditNotes
    ;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const ConsultantActionCreators = {
    requestConsultants: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONSULTANTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Consultants`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Consultant[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONSULTANTS', consultants: data });
                            });
                    }
                });
        }
    },
    requestConsultantsInvoices: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONSULTANTSINVOICES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/ConsultantsInvoices`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ConsultantInvoice[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONSULTANTSINVOICES', consultantsInvoices: data });
                            });
                    }
                });
        }
    },
    requestConsultantsDocuments: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONSULTANTSDOCUMENTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/ConsultantsDocuments`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ConsultantDocument[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONSULTANTSDOCUMENTS', consultantsDocuments: data });
                            });
                    }
                });
        }
    },
    requestConsultantDocument: (id: string, msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/ConsultantsDocuments/` + id, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ConsultantDocument>;
                            })
                            .then(data => {
                                dispatch({ type: 'PATCH_CONSULTANTDOCUMENT', consultantDocument: data });
                            });
                    }
                });
        }
    },
    requestFixePriceConsultants: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_FIXEPRICECONSULTANT' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/FixePriceConsultant`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<FixePriceConsultant[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_FIXEPRICECONSULTANT', fixePriceConsultants: data });
                            });
                    }
                });
        }
    },
    requestConsultantsPhoto: (graphToken: string): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.consultantsState.consultants) {
            dispatch({ type: 'REQUEST_CONSULTANT_PHOTO' });
            appState.consultantsState.consultants.forEach((c) => {
                if (c.o365id) {
                    fetch(
                        "https://graph.microsoft.com/v1.0/users/" + c.o365id + "/photo/$value",
                        {
                            method: "GET",
                            headers: {
                                accept: "application/image",
                                authorization: "bearer " + graphToken,
                            },
                            mode: "cors",
                            cache: "default",
                        }
                    )
                        .then((response) => {
                            if (response.ok) {
                                return response.blob().then((data) => {
                                    if (data !== null) {
                                        window.URL = window.URL || window.webkitURL;
                                        return window.URL.createObjectURL(data);
                                    }
                                });
                            }
                            //    else {
                            //        throw new Error(`Error ${response.status}: ${response.statusText}`);
                            //    }
                        })
                        .then((photo) => {
                            if (c.consultantid) {
                                dispatch({ type: 'PATCH_CONSULTANT_PHOTO', consultantid: c.consultantid, photo: photo });
                            }
                        })
                        .catch((e) => {
                            console.log(e);
                        });
                }
            });
        }
    },
    requestConsultantsPeppolStatus: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, consultant: Consultant): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.consultantsState.consultants) {
            dispatch({ type: 'REQUEST_CONSULTANT_PEPPOLSTATUS', consultant: consultant });
            const appState = getState();
            if (appState && appState.userNeomyticLogin) {
                TokenHelper.GetToken(msalInstance, teamsState)
                    .then(token => {
                        if (token !== null) {
                            fetch(`api/Consultants/` + consultant.consultantid + "/PeppolStatus", {
                                method: "get",
                                headers: {
                                    "Content-Type": "application/json",
                                    authorization: "bearer " + token,
                                },
                                mode: "cors",
                                cache: "default",
                            })
                                .then(response => {
                                    if (response.status === 403) {
                                        //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                    }
                                    return response.json() as Promise<PeppolStatus>;
                                })
                                .then(data => {
                                    dispatch({ type: 'RECEIVE_CONSULTANT_PEPPOLSTATUS', peppolStatus: data, consultant: consultant });
                                });
                        }
                    });
            }
        }
    },
    requestConsultantEdition: (consultant: Consultant | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CONSULTANT', consultant: consultant });
    },
    requestConsultantActivityPeriodesEdition: (consultant: Consultant | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDITACTIVITYPERIODES_CONSULTANT', consultant: consultant });
    },
    patchConsultantEdition: (consultant: Consultant): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONSULTANT', consultant: consultant });
    },
    ReloadConsultantsInvoices: (): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_CONSULTANTSINVOICES' });
    },
    requestConsultantInvoiceEdition: (consultantInvoice: ConsultantInvoice | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CONSULTANTINVOICE', consultantInvoice: consultantInvoice });
    },
    patchConsultantInvoiceEdition: (consultantInvoice: ConsultantInvoice): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONSULTANTINVOICE', consultantInvoice: consultantInvoice });
    },
    requestConslutantQuickView: (consultant: Consultant | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_CONSULTANT', consultant: consultant });
    },
    requestConsultantDocumentCreation: (consultantDocument: ConsultantDocument | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'CREATE_CONSULTANTDOCUMENT', consultantDocument: consultantDocument });
    },
    requestConsultantDocumentEdition: (consultantDocument: ConsultantDocument | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CONSULTANTDOCUMENT', consultantDocument: consultantDocument });
    },
    patchConsultantDocumentEdition: (consultantDocument: ConsultantDocument): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONSULTANTDOCUMENT', consultantDocument: consultantDocument });
    },
    requestConsultantDocumentdeletion: (consultantDocument: ConsultantDocument | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_CONSULTANTDOCUMENT', consultantDocument: consultantDocument });
    },
    requestConsultantPeppolReceivedInvoicedeletion: (consultantDocument: ConsultantPeppolReceivedInvoice | null): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_CONSULTANTPEPPOLRECEIVEDINVOICE', consultantDocument: consultantDocument });
    },
    requestConsultantDocumentdeletion_id: (id: string): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        const cds = getState().consultantsState.consultantDocuments;
        if (cds) {
            const c = cds.findIndex(e => e.consultantdocumentid === id);
            if (c !== -1) {
                dispatch({ type: 'PATCHDELETE_CONSULTANTDOCUMENT', consultantDocument: cds[c] });
            }
        }
    },
    patchConsultantDocumentDeletionEdition: (consultantDocument: ConsultantDocument): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_CONSULTANTDOCUMENT', consultantDocument: consultantDocument });
    },
    patchConsultantPeppolReceivedInvoiceDeletionEdition: (consultantDocument: ConsultantPeppolReceivedInvoice): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_CONSULTANTPEPPOLRECEIVEDINVOICE', consultantDocument: consultantDocument });
    },
    RequestConsultantPeppolReceivedInvoices: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONSULTANT_PEPPOLRECEIVEDINVOICES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/PeppolReceivedInvoices`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ConsultantPeppolReceivedInvoice[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDINVOICES', consultantsDocuments: data });
                            });
                    }
                });
        }
    },
    patchConsultantPeppolReceivedInvoices: (consultantDocument: ConsultantPeppolReceivedInvoice): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONSULTANT_PEPPOLRECEIVEDINVOICES', consultantDocument: consultantDocument });
    },
    RequestConsultantPeppolReceivedCreditNotes: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownConsultantAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/PeppolReceivedCreditNotes`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ConsultantPeppolReceivedCreditNote[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES', consultantsDocuments: data });
                            });
                    }
                });
        }
    },
    patchConsultantPeppolReceivedCreditNotes: (consultantDocument: ConsultantPeppolReceivedCreditNote): AppThunkAction<KnownConsultantAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES', consultantDocument: consultantDocument });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducerConsultants: Reducer<ConsultantsState> = (state: ConsultantsState | undefined, incomingAction: Action): ConsultantsState => {
    if (state === undefined) {
        return {
            consultants: undefined, consultantsLoading: false, consultantEdit: null, consultantActivityPeriodesEdit: null, consultantsPhotoLoaded: false,
            consultantsInvoices: undefined, consultantInvoiceEdit: null, consultantsInvoicesLoading: false,
            fixePriceConsultants: undefined, fixePriceConsultantsLoading: false,
            consultantQuickView: null,
            consultantDocuments: undefined, consultantDocumentsLoading: false, consultantDocumentCreate: null, consultantDocumentEdit: null, consultantDocumentDelete: null, consultantPeppolReceivedInvoicedeletion: null,
            consultantPeppolReceivedInvoices: undefined, consultantPeppolReceivedInvoicesLoading: false,
            consultantPeppolReceivedCreditNotes: undefined, consultantPeppolReceivedCreditNotesLoading: false,
        };
    }

    const action = incomingAction as KnownConsultantAction;
    switch (action.type) {
        case 'REQUEST_CONSULTANTS':
            return { ...state, consultantsLoading: true };
        case 'RECEIVE_CONSULTANTS':
            return { ...state, consultantsLoading: false, consultants: action.consultants.sort(SortHelper.ConsultantByName) };
        case 'RELOAD_CONSULTANTSINVOICES':
            return { ...state, consultantsInvoices: undefined };
        case 'REQUEST_CONSULTANTSINVOICES':
            return { ...state, consultantsInvoicesLoading: true };
        case 'RECEIVE_CONSULTANTSINVOICES':
            return { ...state, consultantsInvoicesLoading: false, consultantsInvoices: action.consultantsInvoices };
        case 'REQUEST_FIXEPRICECONSULTANT':
            return { ...state, fixePriceConsultantsLoading: true };
        case 'RECEIVE_FIXEPRICECONSULTANT':
            return { ...state, fixePriceConsultantsLoading: false, fixePriceConsultants: action.fixePriceConsultants };
        case 'EDIT_CONSULTANT':
            return { ...state, consultantEdit: action.consultant };
        case 'EDITACTIVITYPERIODES_CONSULTANT':
            return { ...state, consultantActivityPeriodesEdit: action.consultant };
        case 'PATCH_CONSULTANT': {
            const ns = { ...state };
            if (ns.consultants && action.consultant.consultantid) {
                const c = ns.consultants.findIndex(e => e.consultantid === action.consultant.consultantid);
                if (c !== -1) {
                    ns.consultants[c] = action.consultant;
                }
                else {
                    ns.consultants.push(action.consultant);
                }
                ns.consultants = [...ns.consultants.sort(SortHelper.ConsultantByName)];
                return ns;
            }
            return state;
        }
        case 'EDIT_CONSULTANTINVOICE':
            return { ...state, consultantInvoiceEdit: action.consultantInvoice };
        case 'PATCH_CONSULTANTINVOICE': {
            const ns = { ...state };
            if (ns.consultantsInvoices && action.consultantInvoice.consultantInvoiceid) {
                const c = ns.consultantsInvoices.findIndex(e => e.consultantInvoiceid === action.consultantInvoice.consultantInvoiceid);
                if (c !== -1) {
                    ns.consultantsInvoices[c] = action.consultantInvoice;
                }
                else {
                    ns.consultantsInvoices.push(action.consultantInvoice);
                }
                ns.consultantsInvoices = [...ns.consultantsInvoices];
                return ns;
            }
            return state;
        }
        case 'REQUEST_CONSULTANT_PHOTO':
            return { ...state, consultantsPhotoLoaded: true };
        case 'PATCH_CONSULTANT_PHOTO': {
            const ns = { ...state };
            if (ns.consultants) {
                const c = ns.consultants.findIndex(e => e.consultantid === action.consultantid);
                if (c !== -1) {
                    ns.consultants[c].photo = action.photo;
                }
                ns.consultants = [...ns.consultants.sort(SortHelper.ConsultantByName)];
                return ns;
            }
            return state;
        }
        case 'QV_CONSULTANT': {
            return { ...state, consultantQuickView: action.consultant };
        }
        case 'REQUEST_CONSULTANTSDOCUMENTS':
            return { ...state, consultantDocumentsLoading: true };
        case 'RECEIVE_CONSULTANTSDOCUMENTS':
            return { ...state, consultantDocumentsLoading: false, consultantDocuments: action.consultantsDocuments.sort(SortHelper.ConsultantDocumentByDate) };
        case 'CREATE_CONSULTANTDOCUMENT':
            return { ...state, consultantDocumentCreate: action.consultantDocument };
        case 'EDIT_CONSULTANTDOCUMENT':
            return { ...state, consultantDocumentEdit: action.consultantDocument };
        case 'PATCH_CONSULTANTDOCUMENT': {
            const ns = { ...state };
            if (ns.consultantDocuments && action.consultantDocument.consultantdocumentid) {
                const c = ns.consultantDocuments.findIndex(e => e.consultantdocumentid === action.consultantDocument.consultantdocumentid);
                if (c !== -1) {
                    ns.consultantDocuments[c] = action.consultantDocument;
                }
                else {
                    ns.consultantDocuments.push(action.consultantDocument);
                }
                ns.consultantDocuments = [...ns.consultantDocuments.sort(SortHelper.ConsultantDocumentByDate)];
                return ns;
            }
            return state;
        }
        case 'DELETE_CONSULTANTDOCUMENT':
            return { ...state, consultantDocumentDelete: action.consultantDocument };
        case 'DELETE_CONSULTANTPEPPOLRECEIVEDINVOICE':
            return { ...state, consultantPeppolReceivedInvoicedeletion: action.consultantDocument };
        case 'PATCHDELETE_CONSULTANTDOCUMENT': {
            const ns = { ...state };
            if (ns.consultantDocuments && action.consultantDocument.consultantdocumentid) {
                ns.consultantDocuments = ns.consultantDocuments.filter((item) => item.consultantdocumentid !== action.consultantDocument.consultantdocumentid);
                ns.consultantDocuments = [...ns.consultantDocuments.sort(SortHelper.ConsultantDocumentByDate)];
                return ns;
            }
            return state;
        }
        case 'PATCHDELETE_CONSULTANTPEPPOLRECEIVEDINVOICE': {
            const ns = { ...state };
            if (ns.consultantPeppolReceivedInvoices && action.consultantDocument.peppolReceivedInvoiceId) {
                ns.consultantPeppolReceivedInvoices = ns.consultantPeppolReceivedInvoices.filter((item) => item.peppolReceivedInvoiceId !== action.consultantDocument.peppolReceivedInvoiceId);
                return ns;
            }
            return state;
        }
        case 'REQUEST_CONSULTANT_PEPPOLSTATUS': {
            const ns = { ...state };
            if (ns.consultants) {
                const c = ns.consultants.findIndex(e => e.consultantid === action.consultant.consultantid);
                if (c !== -1) {
                    ns.consultants[c].peppolStatus = { prod: [], test: [] };
                }
                return ns;
            }
            return state;
        }
        case 'RECEIVE_CONSULTANT_PEPPOLSTATUS': {
            const ns = { ...state };
            if (ns.consultants) {
                const c = ns.consultants.findIndex(e => e.consultantid === action.consultant.consultantid);
                if (c !== -1) {
                    ns.consultants[c].peppolStatus = action.peppolStatus;
                }
                if (state.consultantQuickView && state.consultantQuickView.consultantid === action.consultant.consultantid) {
                    ns.consultantQuickView = { ...ns.consultants[c] };
                }
                ns.consultants = [...ns.consultants.sort(SortHelper.ConsultantByName)];
                return ns;
            }
            return state;
        }
        case 'REQUEST_CONSULTANT_PEPPOLRECEIVEDINVOICES':
            return { ...state, consultantPeppolReceivedInvoicesLoading: true };
        case 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDINVOICES':
            return { ...state, consultantPeppolReceivedInvoicesLoading: false, consultantPeppolReceivedInvoices: action.consultantsDocuments };
        case 'PATCH_CONSULTANT_PEPPOLRECEIVEDINVOICES': {
            const ns = { ...state };
            if (ns.consultantPeppolReceivedInvoices && action.consultantDocument.peppolReceivedInvoiceId) {
                const c = ns.consultantPeppolReceivedInvoices.findIndex(e => e.peppolReceivedInvoiceId === action.consultantDocument.peppolReceivedInvoiceId);
                if (c !== -1) {
                    ns.consultantPeppolReceivedInvoices[c] = action.consultantDocument;
                }
                else {
                    ns.consultantPeppolReceivedInvoices.push(action.consultantDocument);
                }
                ns.consultantPeppolReceivedInvoices = [...ns.consultantPeppolReceivedInvoices];
                return ns;
            }
            return state;
        }
        case 'REQUEST_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES':
            return { ...state, consultantPeppolReceivedCreditNotesLoading: true };
        case 'RECEIVE_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES':
            return { ...state, consultantPeppolReceivedCreditNotesLoading: false, consultantPeppolReceivedCreditNotes: action.consultantsDocuments };
        case 'PATCH_CONSULTANT_PEPPOLRECEIVEDCREDITNOTES': {
            const ns = { ...state };
            if (ns.consultantPeppolReceivedCreditNotes && action.consultantDocument.peppolReceivedCreditNoteId) {
                const c = ns.consultantPeppolReceivedCreditNotes.findIndex(e => e.peppolReceivedCreditNoteId === action.consultantDocument.peppolReceivedCreditNoteId);
                if (c !== -1) {
                    ns.consultantPeppolReceivedCreditNotes[c] = action.consultantDocument;
                }
                else {
                    ns.consultantPeppolReceivedCreditNotes.push(action.consultantDocument);
                }
                ns.consultantPeppolReceivedCreditNotes = [...ns.consultantPeppolReceivedCreditNotes];
                return ns;
            }
            return state;
        }

        default: {
            break;
        }
    }
    // For the QuickView is selected on an other, clear this one
    const actionData = incomingAction as KnownDataAction;
    switch (actionData.type) {
        case 'QV_CLEAR':
        case 'QV_INVOICE':
        case 'QV_PROJECT':
        case 'QV_PROJECTASSIGMENT':
        case 'QV_TIMESHEET':
        case 'QV_PRODUCT': {
            return { ...state, consultantQuickView: null };
        }
    }
    const actionContact = incomingAction as KnownContactsAction;
    switch (actionContact.type) {
        case 'QV_CONTACT': {
            return { ...state, consultantQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionCustomer = incomingAction as KnownCustomersAction;
    switch (actionCustomer.type) {
        case 'QV_CUSTOMER': {
            return { ...state, consultantQuickView: null };
        }
        default: {
            break;
        }
    }
    return state;
};